import {memo, useMemo, useState} from 'react';
import Image from 'next/image';
import Link from 'next/link';
import classNames from 'classnames';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';

import {api} from '@utils/api';
import {useApp} from '@context/AppProvider';
import Loading from '@components/Loading';
import {cartData} from '@utils/functions';

import Attribute from '../../Attribute';

const Item = props => {
	const {styles, item, country, dispatch} = props;
	const [loading, setLoading] = useState(false);
	const getVarientImage = useMemo(() => {
		let image = '/assets/svg/placeholder.svg';
		try {
			const {product_data: product, variance_id: varianceId} = item;
			if (
				product.variances &&
				product.variances.length > 0 &&
				!product.variances[0].is_base_variance
			) {
				const productVariance = product.variances.find(
					item => item._id === varianceId,
				);
				if (productVariance) {
					if (productVariance.image_urls.length) {
						image = productVariance.image_urls.sort(
							(a, b) => a.image_order > b.image_order,
						)[0].src;
					} else {
						image = productVariance.image_urls.sort(
							(a, b) => a.image_order > b.image_order,
						)[0]?.image_url;
					}
				} else if (product.images.length) {
					image = product.images.sort(
						(a, b) => a.image_order > b.image_order,
					)[0]?.image_url;
				}

				if (!image) {
					if (product.images.length > 0) {
						image = product.images[0].image_url;
					}
				}
			} else {
				if (product.images.length > 0) {
					image = product.images[0].image_url;
				}
			}
			return image;
		} catch (e) {
			return image;
		}
	}, [item]);

	const [image, setImage] = useState(getVarientImage);
	const {
		idb: {session, user},
	} = useApp();

	const updateItem = async data => {
		setLoading(true);
		const res = await api(
			{
				url: '/cart/update',
				method: 'POST',
				data,
			},
			session,
		);

		setLoading(false);
		const cartCount =
			res.cart && res.cart.products
				? res.cart.products
						.map(item => item.quantity || 0)
						.reduce((a, b) => a + b, 0)
				: 0;
		const cData = cartData(res, user);
		dispatch({
			type: 'SET_DATA',
			data: {
				cartCount,
				items: cData.items,
				total: cData.total,
				shipping: cData.shipping,
				promo: cData.promo,
			},
		});
	};

	const onAttrUpdate = async (vid, pid, varianceName) => {
		updateItem({
			vid,
			pid,
			// eslint-disable-next-line babel/camelcase
			product_id: item.product_id,
			// eslint-disable-next-line babel/camelcase
			retailer_id: item.retailer_id,
			varianceName,
		});
	};

	const rs = item.product_data.retailer_products.stocks.find(
		i => i.variance_id === item.variance_id,
	);
	const isMax = rs && rs.stock > item.quantity ? false : true;
	return (
		<div className={classNames(styles.bagsItemInst, 'mb-3')}>
			<div className={styles.bagsItemImg}>
				<Image
					width='100'
					height='100'
					src={image}
					alt='shopping-img'
					objectFit='contain'
					onError={e => {
						e.target.style['background-image'] = 'unset';
						setImage('/assets/svg/placeholder.svg');
					}}
				/>
			</div>
			<div className={styles.bagsItemInfo}>
				<span className={styles.bagsItemSubtitle}>
					<Link href={`/brand/${item.product_data.manufacturer.slug}`}>
						{item.product_data.manufacturer.name}
					</Link>
				</span>
				<span className={styles.bagsItemTitle}>
					<Link href={`/product/${item.product_data.slug}`}>
						<a>{item.name}</a>
					</Link>
				</span>
				<span className={styles.bagsItemPrice}>
					AUD $
					{country !== 'New Zealand'
						? item.price.toFixed(2)
						: item?.priceExTax?.toFixed(2)}
				</span>
				<span className={styles.bagsItemRemove}>
					<a
						style={{cursor: 'pointer'}}
						onClick={() => updateItem({remove: item._id})}
					>
						<i className='fa fa-trash fa-lg'></i> Remove
					</a>
				</span>
				<Attribute
					styles={styles}
					product={item}
					productData={item.product_data}
					onChange={onAttrUpdate}
				/>
				<div className={styles.bagsItemEdit}>
					<label>Quantity</label>
					<div className='quantity buttons_added'>
						<button
							type='button'
							className='minus'
							disabled={item.quantity === 1}
							style={{
								cursor: item.quantity === 1 ? 'not-allowed' : 'pointer',
							}}
							onClick={() =>
								updateItem({qty: item.quantity - 1, pid: item._id})
							}
						>
							<span>-</span>
						</button>
						<input
							type='number'
							step='1'
							min='1'
							name='quantity'
							title='Qty'
							className='input-text qty text'
							value={item.quantity}
							size='4'
							onChange={() => {}}
						/>
						{isMax ? (
							<OverlayTrigger
								placement='bottom'
								overlay={
									<Tooltip id={item._id}>
										{rs?.stock > 0
											? `Only ${rs.stock} ${
													rs.stock > 1 ? 'products' : 'product'
											  } in stock`
											: 'Out of stock'}
									</Tooltip>
								}
							>
								<button type='button' className='plus'>
									<span>+</span>
								</button>
							</OverlayTrigger>
						) : (
							<button
								type='button'
								className='plus'
								onClick={() =>
									updateItem({qty: item.quantity + 1, pid: item._id})
								}
							>
								<span>+</span>
							</button>
						)}
					</div>
				</div>
			</div>
			{loading && (
				<div className={styles.outerOverlay}>
					<div className={styles.positionRel}>
						<div className={styles.overlay}></div>
						<div className={styles.loaderPosition}>
							<Loading.LineLoader />
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default memo(Item);
